import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// The expanded rail's chat list (shared/_rail_chats): collapsible Pinned /
// Recents headings, and one ⋯ menu shared by every row. The rail clips
// sideways, so the menu is position: fixed and placed next to the button
// that opened it. Pin and Delete reload the page (the lists re-sort on the
// server); Rename edits the row in place.
const COLLAPSED_KEY = "rail_chats_collapsed"

export default class extends Controller {
    static targets = ["section", "label", "menu", "pinLabel", "projectMenu", "projectPinLabel", "projectEdit", "projectShare", "projectError", "savedMenu"]
    static values = {
        pinUrl: String,
        renameUrl: String,
        deleteUrl: String,
        afterDeleteUrl: String,
        viewUrl: String,
        activeId: Number
    }

    connect() {
        this._row = null
        this._onDocClick = this._onDocClick.bind(this)
        this._onKey = this._onKey.bind(this)
        this._close = this.closeMenu.bind(this)
        const collapsed = this._collapsed()
        this.sectionTargets.forEach((section) => this._setSection(section, !collapsed.includes(section.dataset.section)))
    }

    disconnect() {
        this.closeMenu()
    }

    toggleSection(event) {
        const section = event.currentTarget.closest("[data-rail-chats-target='section']")
        const open = section.classList.contains("is-collapsed")
        this._setSection(section, open)
        this._saveSections()
    }

    expandAll() {
        this.sectionTargets.forEach(section => this._setSection(section, true))
        this._saveSections()
    }

    collapseAll() {
        this.sectionTargets.forEach(section => this._setSection(section, false))
        this._saveSections()
    }

    _saveSections() {
        const rendered = new Set(this.sectionTargets.map(section => section.dataset.section))
        const collapsed = this._collapsed().filter(key => !rendered.has(key))
        collapsed.push(...this.sectionTargets.filter(section => section.classList.contains("is-collapsed"))
            .map(section => section.dataset.section))
        try {
            window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed))
        } catch {
            // Section controls still work when storage is unavailable.
        }
    }

    openMenu(event) {
        event.preventDefault()
        event.stopPropagation()
        const row = event.currentTarget.closest(".rail-chat")
        if (this._row === row) return this.closeMenu()

        this.closeMenu()
        this._row = row
        this.menuTarget.querySelector(".rail-chat-menu-item--danger").hidden = row.dataset.canDelete === "false"
        row.classList.add("is-menu-open")
        this.pinLabelTarget.textContent = row.dataset.pinned === "true" ? "Unpin" : "Pin"

        this.showMenu(this.menuTarget, event)
    }

    openProjectMenu(event) {
        event.preventDefault()
        event.stopPropagation()
        const row = event.currentTarget.closest(".rail-chat-project-group")
        if (this._row === row) return this.closeMenu()
        this.closeMenu()
        this._row = row
        row.classList.add("is-menu-open")
        this.projectPinLabelTarget.textContent = row.dataset.pinned === "true" ? "Unpin" : "Pin"
        this.projectEditTarget.hidden = !row.dataset.editUrl
        if (row.dataset.editUrl) this.projectEditTarget.href = row.dataset.editUrl
        this.projectShareTarget.href = `${row.dataset.projectUrl}?share=1`
        this.projectErrorTarget.hidden = true
        this.showMenu(this.projectMenuTarget, event)
    }

    async toggleProjectPin(event) {
        const row = this._row
        if (!row) return
        const button = event.currentTarget
        button.disabled = true
        const ok = await this._post(row.dataset.pinUrl, { pinned: row.dataset.pinned !== "true" })
        button.disabled = false
        if (ok) { this.closeMenu(); this._reload() }
        else this.projectErrorTarget.hidden = false
    }

    showMenu(menu, event) {
        this._activeMenu = menu
        menu.hidden = false
        menu.showPopover()
        const anchor = event.currentTarget.getClientRects().length ? event.currentTarget : event.currentTarget.closest(".rail-chat, .rail-project-row")
        const rect = anchor.getBoundingClientRect()
        const width = menu.offsetWidth
        const height = menu.offsetHeight
        const left = Math.min(rect.left, window.innerWidth - width - 8)
        const below = rect.bottom + 4
        menu.style.left = `${Math.max(8, left)}px`
        menu.style.top = `${below + height > window.innerHeight - 8 ? rect.top - height - 4 : below}px`
        menu.querySelector("button")?.focus({ preventScroll: true })

        document.addEventListener("click", this._onDocClick)
        document.addEventListener("keydown", this._onKey)
        window.addEventListener("resize", this._close)
        this.element.closest(".assistant-bottom-nav")?.addEventListener("scroll", this._close, { passive: true })
    }

    openSavedMenu(event) {
        event.preventDefault()
        event.stopPropagation()
        const row = event.currentTarget.closest(".rail-saved-pin")
        if (this._row === row) return this.closeMenu()
        this.closeMenu()
        this._row = row
        row.classList.add("is-menu-open")
        this.showMenu(this.savedMenuTarget, event)
    }

    closeMenu() {
        for (const menu of [...this.menuTargets, ...this.projectMenuTargets, ...this.savedMenuTargets]) {
            if (menu.matches(":popover-open")) menu.hidePopover()
            menu.hidden = true
        }
        this._activeMenu = null
        this._row?.classList.remove("is-menu-open")
        this._row = null
        document.removeEventListener("click", this._onDocClick)
        document.removeEventListener("keydown", this._onKey)
        window.removeEventListener("resize", this._close)
        this.element.closest(".assistant-bottom-nav")?.removeEventListener("scroll", this._close)
    }

    async unpinItem(event) {
        const button = event.currentTarget
        const row = button.closest(".rail-saved-pin") || this._row
        if (!row) return
        button.disabled = true
        const ok = await this._request("DELETE", row.dataset.unpinUrl, {})
        if (ok) { this.closeMenu(); this._reload() }
        button.disabled = false
    }

    async togglePin() {
        const row = this._row
        if (!row) return
        this.closeMenu()
        const pinned = row.dataset.pinned !== "true"
        const ok = await this._post(this.pinUrlValue, { conversation_id: row.dataset.conversationId, pinned: pinned ? "1" : "0" })
        if (ok) this._reload()
    }

    // The Recent section's organize toggle: flat latest chats, or a folder per
    // project. The lists re-sort on the server, so this reloads like Pin does.
    async toggleView(event) {
        const button = event.currentTarget
        const projects = button.dataset.current !== "projects"
        button.disabled = true
        const ok = await this._post(this.viewUrlValue, { view: projects ? "projects" : "recent" })
        button.disabled = false
        if (ok) this._reload()
    }

    rename() {
        const row = this._row
        if (!row) return
        const section = row.closest(".rail-chats-section")
        this.closeMenu()
        const label = row.querySelector(".rail-chat-label")
        const link = row.querySelector(".rail-chat-link")
        const before = label.textContent
        const input = document.createElement("input")
        input.type = "text"
        input.className = "rail-chat-rename"
        input.value = before
        input.maxLength = 120
        input.setAttribute("aria-label", "Chat name")
        link.hidden = true
        row.classList.add("is-renaming")
        link.after(input)
        input.focus()
        input.select()

        let done = false
        const finish = async (save) => {
            if (done) return
            done = true
            const title = input.value.trim()
            input.remove()
            link.hidden = false
            row.classList.remove("is-renaming")
            if (!save || title === before) return
            label.textContent = title || before
            const ok = await this._post(this.renameUrlValue, { conversation_id: row.dataset.conversationId, title })
            if (!ok) label.textContent = before
            else if (!title) this._reload()
            else {
                link.title = title
                section.dispatchEvent(new CustomEvent("sidebar:mutated", { detail: { id: row.dataset.conversationId, title } }))
            }
        }
        input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") { e.preventDefault(); finish(true) }
            if (e.key === "Escape") { e.preventDefault(); finish(false) }
        })
        input.addEventListener("blur", () => finish(true))
    }

    async destroy() {
        const row = this._row
        if (!row) return
        const section = row.closest(".rail-chats-section")
        this.closeMenu()
        if (!window.confirm("Delete this conversation for good? This can't be undone.")) return

        const id = row.dataset.conversationId
        const ok = await this._request("DELETE", this.deleteUrlValue, { conversation_id: id })
        if (!ok) return
        if (Number(id) === this.activeIdValue) {
            window.location.assign(this.afterDeleteUrlValue)
        } else {
            section.dispatchEvent(new CustomEvent("sidebar:mutated", { detail: { id, deleted: true } }))
            row.remove()
            section.dispatchEvent(new Event("sidebar:refresh"))
        }
    }

    _onDocClick(event) {
        if (!this._activeMenu?.contains(event.target)) this.closeMenu()
    }

    _onKey(event) {
        if (event.key === "Escape") {
            const button = this._row?.querySelector(".rail-chat-more")
            this.closeMenu()
            button?.focus()
        }
    }

    _setSection(section, open) {
        section.classList.toggle("is-collapsed", !open)
        section.querySelector(".rail-chats-heading")?.setAttribute("aria-expanded", String(open))
    }

    _collapsed() {
        try {
            const parsed = JSON.parse(window.localStorage.getItem(COLLAPSED_KEY) || "[]")
            return Array.isArray(parsed) ? parsed : []
        } catch {
            return []
        }
    }

    _post(url, body) {
        return this._request("POST", url, body)
    }

    async _request(method, url, body) {
        try {
            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
                credentials: "same-origin",
                body: JSON.stringify(body)
            })
            return response.ok
        } catch {
            return false
        }
    }

    _reload() {
        if (window.Turbo?.visit) window.Turbo.visit(window.location.href, { action: "replace" })
        else window.location.reload()
    }
}
